import { TableViewParameters } from '@/models/TableView'
import {
  VehicleListResponse,
} from '@/models/dto/TableViewVehicle'
import {
  ApiResult,
  AssignableVehicle,
  AssignableVehiclesGetResponse,
  DecalStatusType,
  DeleteVehiclePhotoPayload,
  TableViewVehicle,
  VehicleDetailEntity,
  VehicleDetailResponse,
  VehiclePhotoDTO,
  VehicleValidationResult,
  VehicleProfileCounts,
  VehicleAmenityAlert,
} from '@/models/dto'
import { GenericApiResult } from '@/models/dto/GenericApiResult'
import { Tableview } from '@/models/dto/Tableview'
import { TrackingSource } from '@/models/dto/Tracking'
import { MultiVehicleAmenityRequirementMatchResult } from '@/models/dto/VehicleAmenityRequirements'
import vehicleDetail from '@/store/modules/vehicleDetail'
import auth from '@/store/modules/auth'
import { UploadRequirements } from '@/utils/amenityReview'
import { toAssignableVehicle } from '@/utils/vehicle'
import { bffProxyUrl } from '@/utils/env'
import { AxiosProgressEvent, AxiosResponse } from 'axios'
import Vue from 'vue'

export default {
  /**
   * Gets a paginated list of vehicles for the current company.
   * @param params - Pagination, sorting, and filtering parameters.
   * @returns the structured vehicle list response.
   */
  getCompanyVehicles(
    params: TableViewParameters
  ): Promise<AxiosResponse<VehicleListResponse>> {
    const url = `${bffProxyUrl}/companies/${auth.user.companyId}/vehicles`
    return Vue.prototype.$http.post(url, params)
  },

  /**
   * Gets the vehicles that can be assigned to a reservation, scoped to the vehicle types the
   * reservation requires.
   * @param reservationId - The ID of the reservation to fetch assignable vehicles for.
   * @returns the assignable vehicles, each flagged with its availability for the trip.
   */
  async getAssignableVehicles(
    reservationId: number
  ): Promise<AssignableVehicle[]> {
    const url = `${bffProxyUrl}/reservations/${reservationId}/companies/${auth.user.companyId}/vehicles/assignment`
    const response = await Vue.prototype.$http.get<AssignableVehiclesGetResponse>(url)
    return response.data.vehicles.map(toAssignableVehicle)
  },

  /**
   * Gets the vehicle table view.
   * @param params - the parameters for the table view.
   * @returns the table view result of vehicles.
   */
  tableView(
    params: TableViewParameters
  ): Promise<AxiosResponse<Tableview<TableViewVehicle>>> {
    const { sorts = null, filters = null, pageSize = 10, page = 1 } = params
    let query = `page=${page}&pageSize=${pageSize}`
    if (sorts) {
      query = `${query}&${sorts}`
    }
    if (filters) {
      query = `${query}&${filters}`
    }
    query = encodeURI(query)
    const url = `${bffProxyUrl}/tables/vehicles?${query}`
    return Vue.prototype.$http.get(url)
  },

  /**
   * @returns the table view of all vehicles associated with the current company.
   */
  allVehicles(): Promise<AxiosResponse<Tableview<TableViewVehicle>>> {
    // This param is a hack to get all vehicles
    return this.tableView({ pageSize: -1 })
  },

  /**
   * Gets a vehicle by ID.
   * @param vehicleId - The ID of the vehicle to fetch.
   * @returns the vehicle detail.
   */
  byId(vehicleId: number): Promise<AxiosResponse<VehicleDetailResponse>> {
    const url = `${bffProxyUrl}/vehicles/${vehicleId}?expand=amenity_reviews,profile_status`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Creates a vehicle.
   * @param vehicle - The vehicle to create.
   * @returns the ID of the created vehicle.
   */
  create(
    vehicle: VehicleDetailEntity
  ): Promise<AxiosResponse<GenericApiResult<number>>> {
    const url = `${bffProxyUrl}/vehicles`
    return Vue.prototype.$http.post(url, vehicle)
  },
  /**
   * Updates a vehicle.
   * @param vehicle - The vehicle to update.
   */
  update(vehicle: VehicleDetailEntity): Promise<AxiosResponse<ApiResult>> {
    const url = `${bffProxyUrl}/vehicles/${vehicle.vehicleId}`
    return Vue.prototype.$http.put(url, vehicle)
  },
  /**
   * Deletes a vehicle.
   * @param vehicleId - The ID of the vehicle to delete.
   * @returns the result of the delete operation.
   */
  delete(vehicleId: number): Promise<AxiosResponse<ApiResult>> {
    const url = `${bffProxyUrl}/vehicles/${vehicleId}`
    return Vue.prototype.$http.delete(url)
  },
  /**
   * Uploads vehicle photos.
   * @param vehicleId - The ID of the vehicle to upload photos for.
   * @param form - The form data containing the photos to upload.
   * @returns the result of the upload operation.
   */
  uploadPhotos(
    vehicleId: number,
    form: FormData
  ): Promise<AxiosResponse<boolean>> {
    const url = `${bffProxyUrl}/vehicles/${vehicleId}/vehiclePhotos`
      // noinspection JSUnusedGlobalSymbols
      const config = {
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (progressEvent.total) {
          const uploadPercentage = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          )
          vehicleDetail.setUploadPercentage(uploadPercentage)
        }
      },
    }
    return Vue.prototype.$http.post(url, form, config)
  },
  /**
   * Sets and existing vehicle photo as the default photo.
   * @param vehicleId - the ID of the vehicle to set the default photo for.
   * @param vehiclePhoto - the vehicle photo to set as the default.
   */
  makeVehiclePhotoDefault(
    vehicleId: number,
    vehiclePhoto: VehiclePhotoDTO
  ): Promise<AxiosResponse> {
    const url = `${bffProxyUrl}/vehicles/${vehicleId}/updateVehiclePhoto`
    return Vue.prototype.$http.patch(url, vehiclePhoto)
  },
  /**
   * Deletes vehicle photos.
   * @param vehicleId - the ID of the vehicle to delete photos for.
   * @param payload - the payload containing the photos to delete.
   * @returns the result of the delete operation.
   */
  deleteVehiclePhotos(
    vehicleId: number,
    payload: DeleteVehiclePhotoPayload
  ): Promise<AxiosResponse<ApiResult>> {
    const url = `${bffProxyUrl}/vehicles/${vehicleId}/removeVehiclePhotos`
    return Vue.prototype.$http.delete(url, {
      data: payload,
    })
  },

  /**
   * Gets device and gps data for vehicles.
   * @param vehicles - the list of vehicle objects to search for.
   * @returns the tracking source data for the input vehicles.
   */
  getVehicleTrackingData(
    vehicles: VehicleDetailEntity[]
  ): Promise<AxiosResponse<GenericApiResult<TrackingSource>>> {
    const url = `${bffProxyUrl}/v2/tracking/deviceInfo`
    return Vue.prototype.$http.post(url, vehicles)
  },

  /**
   * Checks if a VIN is valid and returns make, model, and model year information encoded in the VIN
   * @param vin - the vin to validate and decode
   * @param vehicleId - the id of the vehicle the vin would get set to if the vehicle already exists in the db
   * @returns the make, model, and model year information if the VIN is valid, an error message if it is not
   */
  validateVIN(
    vin: string,
    vehicleId: number
  ): Promise<AxiosResponse<VehicleValidationResult>> {
    let url = null
    if (!vehicleId) {
      url = `${bffProxyUrl}/vehicles/validateVIN/${vin}`
    } else {
      url = `${bffProxyUrl}/vehicles/validateVIN/${vin}?vehicleId=${vehicleId}`
    }
    return Vue.prototype.$http.get(url)
  },

  /**
   * Create a vehicle with a vin review.
   * @param vehicle - the vehicle that will be created with a vin review.
   * @returns the id of the vehicle that was created
   */
  createWithReview(
    vehicle: VehicleDetailEntity
  ): Promise<AxiosResponse<GenericApiResult<number>>> {
    const url = `${bffProxyUrl}/vehicles/createWithReview`
    return Vue.prototype.$http.post(url, vehicle)
  },

  /**
   * Updates a vehicle and creates a vin review.
   * @param vehicle - The vehicle to update.
   */
  updateWithReview(
    vehicle: VehicleDetailEntity
  ): Promise<AxiosResponse<ApiResult>> {
    const url = `${bffProxyUrl}/vehicles/updateWithReview/${vehicle.vehicleId}`
    return Vue.prototype.$http.put(url, vehicle)
  },
  compareMultipleVehicleAmenitiesToTripAmenities(
    vehicleIds: number[],
    referralId: number
  ): Promise<AxiosResponse<MultiVehicleAmenityRequirementMatchResult>> {
    const vehicleIdString = vehicleIds.join(',')
    const url = `${bffProxyUrl}/vehicles/reservations/${referralId}/amenity-compatibility?vehicleIds=${vehicleIdString}`
    return Vue.prototype.$http.get(url)
  },
  getVehicleCompleteUpdateThreshold(): Promise<AxiosResponse<string>> {
    const url = `${bffProxyUrl}/vehicles/profiles/complete-update-threshold`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Retrieves all decal status types.
   * @returns a list of decal status types.
   */
  getDecalStatusTypes(): Promise<AxiosResponse<DecalStatusType[]>> {
    const url = `${bffProxyUrl}/vehicles/decals/types`
    return Vue.prototype.$http.get(url)
  },
  getVehicleProfileCounts(): Promise<AxiosResponse<VehicleProfileCounts>> {
    const url = `${bffProxyUrl}/vehicles/profiles/counts`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Gets the number of vehicles belonging to the current company that need attention due to an amenity review awaiting proof.
   * @returns the number of vehicles that need attention.
   */
  getNeedsAttentionVehicleCount(): Promise<AxiosResponse<number>> {
    const url = `${bffProxyUrl}/vehicles/needs-attention/count`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Gets the photo upload requirements.
   * @returns the photo requirements including max size and supported mime types.
   */
  getPhotoRequirements(): Promise<
    AxiosResponse<{ maxSizeBytes: number; supportedMimeTypes: string[] }>
  > {
    const url = `${bffProxyUrl}/vehicles/photos/requirements`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Gets the upload requirements for amenity review submissions.
   * @returns the upload requirements by category including max size and supported mime types.
   */
  getAmenityReviewUploadRequirements(): Promise<AxiosResponse<UploadRequirements[]>> {
    const url = `${bffProxyUrl}/vehicles/amenities/reviews/submissions/requirements`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Submits proof for an open amenity review.
   * @param vehicleId - The ID of the vehicle to submit proof for.
   * @param amenityId - The ID of the amenity to submit proof for.
   * @param reviewId - The ID of the review to submit proof for.
   * @param submissionNote - A note describing the submitted proof.
   * @param files - The files being submitted as proof.
   */
  submitAmenityReviewProof(
    vehicleId: number,
    amenityId: number,
    reviewId: string,
    submissionNote: string,
    files: File[]
  ): Promise<AxiosResponse<void>> {
    const formData = new FormData()
    formData.append('submissionNote', submissionNote || '')
    files.forEach((file) => {
      formData.append('files', file, file.name)
    })
    
    const url = `${bffProxyUrl}/vehicles/${vehicleId}/amenities/${amenityId}/reviews/${reviewId}/submissions`
    return Vue.prototype.$http.post(url, formData)
  },
  /**
   * Submits proof for an amenity that does not currently have an open review.
   * @param vehicleId - The ID of the vehicle to submit proof for.
   * @param amenityId - The ID of the amenity to submit proof for.
   * @param submissionNote - A note describing the submitted proof.
   * @param files - The files being submitted as proof.
   */
  createAmenityReviewWithProof(
    vehicleId: number,
    amenityId: number,
    submissionNote: string,
    files: File[]
  ) {
    const formData = new FormData()
    formData.append('submissionNote', submissionNote || '')
    files.forEach((file) => {
      formData.append('files', file, file.name)
    })

    const url = `${bffProxyUrl}/vehicles/${vehicleId}/amenities/${amenityId}/reviews`
    return Vue.prototype.$http.post(url, formData)
  },

  getAmenityAlerts(): Promise<AxiosResponse<VehicleAmenityAlert[]>> {
    const url = `${bffProxyUrl}/vehicles/amenities/alerts`
    return Vue.prototype.$http.post(url)
  }
}
